/**
 * ChannelBadge — small inline badge showing the marketing channel (Meta/Google).
 *
 * Used in campaign lists, insight rows, audience entries, etc. to identify
 * which platform a resource belongs to.
 */

import type { MarketingChannel } from '@/types/marketing'

interface ChannelBadgeProps {
  channel?: MarketingChannel | null
  size?: 'sm' | 'md'
  showLabel?: boolean
}

const CHANNEL_CONFIG: Record<
  string,
  { label: string; color: string; bgColor: string; icon: string }
> = {
  meta: {
    label: 'Meta',
    color: 'text-purple-700 dark:text-purple-300',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    icon: '🟣',
  },
  google: {
    label: 'Google',
    color: 'text-blue-700 dark:text-blue-300',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    icon: '🔵',
  },
}

export function ChannelBadge({ channel, size = 'sm', showLabel = true }: ChannelBadgeProps) {
  if (!channel) return null

  const config = CHANNEL_CONFIG[channel]
  if (!config) return null

  const sizeClasses = size === 'sm' ? 'text-xs px-1.5 py-0.5' : 'text-sm px-2 py-1'

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ${config.bgColor} ${config.color} ${sizeClasses}`}
      title={`${config.label} Ads`}
    >
      <span className="leading-none">{config.icon}</span>
      {showLabel && <span>{config.label}</span>}
    </span>
  )
}

export default ChannelBadge
